import { Fragment, useState } from 'react';
import { IconButton, Menu, MenuItem, Divider, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import INodeAgent from 'types/INodeAgent';

export interface NodeActionsMenuProps {
  node: INodeAgent;
  pendingFlags: {
    isDraining: boolean;
    isResuming: boolean;
    isRemoving: boolean;
  };
  onStartWorker?: () => void;
  onRecycleLocalFiles?: () => void;
  onDrain?: () => void;
  onResume?: () => void;
  onRemove?: () => void;
}

interface ActionItem {
  key: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled: boolean;
  disabledReason?: string;
}

export const NODE_DRAINED_REASON = 'Node is drained — resume it first';
const OFFLINE_REASON = 'Only available while the node agent is online';

function buildItems(props: NodeActionsMenuProps): ActionItem[] {
  const { node, pendingFlags, onStartWorker, onRecycleLocalFiles, onDrain, onResume, onRemove } = props;
  const online = node.state === 'ONLINE';
  const drained = Boolean(node.drain);
  const items: ActionItem[] = [];

  items.push({
    key: 'start-worker',
    label: 'Start worker',
    onClick: onStartWorker ?? (() => {}),
    disabled: !online || drained || !onStartWorker,
    disabledReason: !online ? OFFLINE_REASON : drained ? NODE_DRAINED_REASON : undefined,
  });

  items.push({
    key: 'recycle-local-files',
    label: 'Recycle local files',
    onClick: onRecycleLocalFiles ?? (() => {}),
    disabled: !online || !onRecycleLocalFiles,
    disabledReason: !online ? OFFLINE_REASON : undefined,
  });

  const drainAgain = drained && (node.drain?.tasks_running ?? 0) > 0;
  const drainBlocked = !online || (drained && !drainAgain);
  items.push({
    key: 'drain',
    label: pendingFlags.isDraining ? 'Draining…' : drainAgain ? 'Drain again' : 'Drain',
    onClick: onDrain ?? (() => {}),
    disabled: drainBlocked || pendingFlags.isDraining || pendingFlags.isResuming || !onDrain,
    disabledReason: pendingFlags.isDraining
      ? undefined
      : !online
        ? OFFLINE_REASON
        : drained
          ? 'Node is already drained and nothing is running on it'
          : undefined,
  });

  const workersStillUp = drained ? node.drain!.workers_total - node.drain!.workers_stopped : 0;
  const resumeBlocked = !drained || !online || workersStillUp > 0;
  items.push({
    key: 'resume',
    label: pendingFlags.isResuming ? 'Resuming…' : 'Resume',
    onClick: onResume ?? (() => {}),
    disabled: resumeBlocked || pendingFlags.isResuming || pendingFlags.isDraining || !onResume,
    disabledReason: pendingFlags.isResuming
      ? undefined
      : !drained
        ? 'No drain in progress on this node'
        : !online
          ? 'Node agent is offline — wait for it to come back before resuming'
          : workersStillUp > 0
            ? `Wait for the retired workers to stop (${workersStillUp} still running)`
            : undefined,
  });

  items.push({
    key: 'remove',
    label: pendingFlags.isRemoving ? 'Removing…' : 'Remove',
    onClick: onRemove ?? (() => {}),
    destructive: true,
    disabled: online || pendingFlags.isRemoving || !onRemove,
    disabledReason: online && !pendingFlags.isRemoving ? 'Only available when the node agent is offline' : undefined,
  });

  return items;
}

export default function NodeActionsMenu(props: NodeActionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const items = buildItems(props);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const firstDestructiveIdx = items.findIndex((i) => i.destructive);

  return (
    <>
      <IconButton size="small" aria-label="Node actions" onClick={handleOpen}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {items.map((item, idx) => {
          const showDivider = firstDestructiveIdx > 0 && idx === firstDestructiveIdx;
          const menuItem = (
            <MenuItem
              disabled={item.disabled}
              onClick={() => {
                handleClose();
                item.onClick();
              }}
              sx={item.destructive ? { color: 'error.main' } : undefined}
            >
              {item.label}
            </MenuItem>
          );
          const rendered =
            item.disabled && item.disabledReason ? (
              <Tooltip title={item.disabledReason} placement="left">
                <span style={{ display: 'block' }}>{menuItem}</span>
              </Tooltip>
            ) : (
              menuItem
            );
          return (
            <Fragment key={item.key}>
              {showDivider && <Divider />}
              {rendered}
            </Fragment>
          );
        })}
      </Menu>
    </>
  );
}
